import React from "react";
import useInfiniteSearch from "hooks/useInfiniteSearch";
import { useCustomQuery } from "data/modules/api/hooks/useCustomQuery";
import { DEFAULT_FORMS_PAGE_SIZE } from "features/CourseAnalytics/modules/ContentResponses/modules/FormsV2/modules/DesktopForms/utils/DesktopView.utils";

const DYNAMIC_LINKS_API = "/payments/exly/dynamiclinks/all";

export const useFetchCustomCodeComboPage = ({
  paymentLinkType = 4,
  initialData = {},
  listingUuid,
} = {}) => {
  const {
    data,
    loading,
    setLoading,
    setError,
    setHasMore,
    pageNumber,
    setPageNumber,
    setData,
    lastElementRef,
  } = useInfiniteSearch();

  const prevListingUuidRef = React.useRef(listingUuid);

  React.useEffect(() => {
    if (prevListingUuidRef.current !== listingUuid) {
      prevListingUuidRef.current = listingUuid;
      setPageNumber(1);
      setData([]);
    }
  }, [listingUuid, setPageNumber, setData]);

  const {
    combos_pages_mapping: combosPagesMapping,
    isActive = undefined,
    currencies = undefined,
  } = initialData;

  const prevCurrenciesRef = React.useRef(currencies);

  // Reset page number and data when currencies change
  React.useEffect(() => {
    if (prevCurrenciesRef.current !== currencies) {
      prevCurrenciesRef.current = currencies;
      setPageNumber(1);
      setData([]);
    }
  }, [currencies, setPageNumber, setData]);

  const {
    data: queryData,
    isFetching,
    isError,
  } = useCustomQuery({
    url: DYNAMIC_LINKS_API,
    queryKey: [
      DYNAMIC_LINKS_API,
      { pageNumber, paymentLinkType, listingUuid, currencies },
    ],
    params: {
      page: pageNumber,
      page_size: DEFAULT_FORMS_PAGE_SIZE,
      payment_link_type: Array.isArray(paymentLinkType)
        ? paymentLinkType.join(",")
        : paymentLinkType,
      ...(isActive ? { is_active: true } : {}),
      ...(currencies ? { currencies: currencies.join(",") } : {}),
      ...(listingUuid ? { listing_uuid: listingUuid } : {}),
    },
    cacheTime: 0,
    enabled: true,
  });

  React.useEffect(() => {
    if (isFetching) setLoading(true);
    if (isError) setError(true);

    if (queryData) {
      const { links = [], total_pages = 1 } = queryData;
      setHasMore(total_pages > pageNumber);

      const formattedLinks = links.map((item) => ({
        ...item,
        description: item.listing_title,
        label: item?.title,
        value: item.dynamic_link_id,
      }));

      // Selected combo mappings
      const selectedFormattedLinks = (combosPagesMapping || []).map((data) => ({
        label: data?.title,
        value: data?.uuid,
        dynamic_link_id: data?.uuid,
        title: data?.title,
        link_price: data?.price,
        currency: data?.currency,
        pop_name: (data?.listings || []).map((lst) => lst?.title).join(", "),
      }));

      if (pageNumber === 1) {
        // Only Appending Selected Data on load pre selected
        const mergedUnique = Array.from(
          new Map(
            [...selectedFormattedLinks, ...formattedLinks].map((item) => [
              item.value,
              item,
            ])
          ).values()
        );
        setData(mergedUnique);
      } else {
        setData((prev) => {
          const merged = [...prev, ...formattedLinks];
          return Array.from(
            new Map(merged.map((item) => [item.value, item])).values()
          );
        });
      }
    }

    if (!isFetching) setLoading(false);
  }, [
    queryData,
    isFetching,
    isError,
    setLoading,
    setError,
    setHasMore,
    setData,
    pageNumber,
    combosPagesMapping,
  ]);

  return {
    data,
    loading,
    lastElementRef,
  };
};
